import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import styled from 'styled-components';
import Link from 'next/link';

import { Caret } from 'shared/icons/Caret';
import { FeatureFlags } from 'featureFlags/constants';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { variableFontRegular } from 'styles/theme/text';
import { trackHeaderBreadcrumbClick } from 'layout/header/utils/trackHeaderBreadcrumbClick';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';

interface IHeaderNavigationList {
  menuItems?: Array<ICMSBreadcrumb>;
  label: string;
  href: string;
  specialNavLabel?: string | null;
}

export const HeaderNavigationList = ({
  menuItems = [],
  label,
  href,
  specialNavLabel = null,
}: IHeaderNavigationList) => {
  const parseLabel = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PARSE_NAV_LABEL);

  const handleTitleClick = () => {
    if (label === 'Timeline') {
      sendTrackingEvent('timeline_tap');
    } else {
      sendTrackingEvent('shop_listings_tap');
    }
  };

  const handleBreadcrumbClick = (title, name, slug) => {
    if (label === 'Timeline') {
      const productTemplateSlug = slug?.split('/')?.[1];
      trackHeaderBreadcrumbClick('Calendar', title, name, productTemplateSlug || slug);
    } else if (specialNavLabel) {
      trackHeaderBreadcrumbClick(specialNavLabel, title, name, slug);
    } else {
      trackHeaderBreadcrumbClick(label, title, name, slug);
    }
  };

  const formattedLabel = (name) => {
    if (name?.includes('[date]')) {
      const itemInfo = name?.split('[date]');
      return (
        <HeaderNavigationList.ChildContainer>
          <HeaderNavigationList.ChildDate>{itemInfo?.[1]}</HeaderNavigationList.ChildDate>
          <span>{itemInfo?.[2]}</span>
        </HeaderNavigationList.ChildContainer>
      );
    }
    return name;
  };

  return (
    <HeaderNavigationList.Wrapper>
      <HeaderNavigationList.TitleAndArrowContainer>
        <Link href={href} passHref prefetch={false}>
          <a
            tabIndex={0}
            role="link"
            data-qa="header_link"
            onClick={handleTitleClick}
            onKeyDown={handleTitleClick}
          >
            {label}
          </a>
        </Link>
        <HeaderNavigationList.CaretWrapper>
          <Caret color="white" direction="right" />
        </HeaderNavigationList.CaretWrapper>
      </HeaderNavigationList.TitleAndArrowContainer>

      <HeaderNavigationList.FlexWrapper>
        {menuItems?.map(({ name, children = [] }) => (
          <HeaderNavigationList.MenuColumn key={name} columnCount={menuItems.length}>
            <HeaderNavigationList.Title>{name}</HeaderNavigationList.Title>
            <HeaderNavigationList.List>
              {children.map(({ name: childName, slug }: { name: string; slug: string }) => (
                <HeaderNavigationList.Link key={slug} $isTimeline={label === 'Timeline'}>
                  <Link href={`/${slug}`} passHref prefetch={false}>
                    <a
                      tabIndex={0}
                      role="link"
                      data-qa="menu_dropdown_link"
                      onClick={() => handleBreadcrumbClick(name, childName, slug)}
                      onKeyDown={() => handleBreadcrumbClick(name, childName, slug)}
                    >
                      {parseLabel ? formattedLabel(childName) : childName}
                    </a>
                  </Link>
                </HeaderNavigationList.Link>
              ))}
            </HeaderNavigationList.List>
          </HeaderNavigationList.MenuColumn>
        ))}
      </HeaderNavigationList.FlexWrapper>
    </HeaderNavigationList.Wrapper>
  );
};

HeaderNavigationList.Wrapper = styled.div`
  padding: 16px;
`;

HeaderNavigationList.CaretWrapper = styled.div`
  margin-left: 6px;
  margin-bottom: 2px;
`;

HeaderNavigationList.TitleAndArrowContainer = styled.div`
  display: flex;
  align-items: center;
  ${variableFontRegular({ size: '11.5px' })};

  :hover {
    text-decoration: underline;
    text-underline-offset: 2px;
    outline: none;
  }

  a {
    ${variableFontRegular({ size: '11.5px' })};
  }
`;

HeaderNavigationList.FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 40px;
`;

HeaderNavigationList.MenuColumn = styled.div<{ columnCount?: number }>`
  margin-bottom: 32px;
  margin-right: 32px;
  min-width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};
  width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};

  :last-child {
    margin-right: 0;
  }
`;

HeaderNavigationList.Title = styled.h4`
  margin-bottom: 24px;
  cursor: default;
  font-weight: 400;
  ${variableFontRegular({ size: '9.5px', height: '11px' })};
`;

HeaderNavigationList.List = styled.div`
  min-width: calc(100vw - 64px);
  display: flex;
  flex-wrap: wrap;
`;

HeaderNavigationList.Link = styled.div<{ $isTimeline: boolean }>`
  ${variableFontRegular({ size: '11.5px' })};
  width: ${({ $isTimeline }) => ($isTimeline ? `100%` : `50%`)};

  > a {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 245px;
    padding-bottom: 8px;

    ${variableFontRegular({ size: '11.5px' })};

    :hover {
      text-decoration: underline;
      text-underline-offset: 2px;
      outline: none;
    }
  }

  :last-child {
    margin-bottom: 0;
  }
`;

HeaderNavigationList.ChildContainer = styled.div`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 245px;
`;

HeaderNavigationList.ChildDate = styled.span`
  min-width: 40px;
  width: 40px;
  margin-right: 4px;
  display: inline-block;
`;
